import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Flex, HStack, Spinner, Text, useToast } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import AxisStep from "./AxisStep";
import AdvantageStep from "./AdvantageStep";
import ConfirmStep from "./ConfirmStep";
import AiStep from "./AiStep";
import {
  AXIS_ORDER,
  AxisKey,
  FactorDef,
  INITIAL_STATE,
  SUB_TAB_DEFS,
  SalesDataLinkageInputState,
  SalesDataLinkageSubView,
} from "./types";
import { isAdvantageStepComplete, isAxisStepComplete, isConfirmStepComplete } from "./utils";
import { WebAPIContext } from "../../../contexts/WebAPIContext";
import { ProjectContext } from "../InvestmentScreeningDetail";

/** design_handoff準拠のシェブロン型ステッパーの1ステップ分クリップパス */
const chevronClip = (isFirst: boolean, isLast: boolean): string => {
  if (isFirst && isLast) return "none";
  if (isFirst) return "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)";
  if (isLast) return "polygon(0 0, 100% 0, 100% 100%, 0 100%, 14px 50%)";
  return "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)";
};

interface Props {
  isEditable?: boolean;
  /** 翻訳キーのプレフィックス（画面名）。features.InvestmentScreening.<namespace>.salesDataLinkage.* を参照する */
  namespace: string;
  /** 営業データ連携の検索クエリに渡すプロジェクトID */
  projectId?: string;
  /** AI試算結果（要因別ストレス）をストレス指数（ダウンサイド指数）へ反映する際のコールバック */
  onReflect?: (factors: FactorDef[]) => void;
  /** 入力内容が初期状態から変更されている（未保存の変更がある）かどうかを親へ通知するコールバック */
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * 営業データ連携タブのルートコンポーネント。
 * 市場/業界/製品/客先 → 優位性 → 入力確認 → AI分析 のステップ構成。
 * 本フェーズはフロントエンドのみの実装であり、状態はこのコンポーネント内に閉じる（モック）。
 */
/** 未保存判定の比較から除外するフィールド（画面遷移・内部管理用で「入力内容」ではないもの） */
const DIRTY_CHECK_IGNORE_KEYS = ["subView", "updateDate", "hasLoadedFromServer", "reflectConfirm"] as const;

const stripForDirtyCheck = (s: SalesDataLinkageInputState): Partial<SalesDataLinkageInputState> => {
  const copy: Partial<SalesDataLinkageInputState> = { ...s };
  DIRTY_CHECK_IGNORE_KEYS.forEach((k) => delete copy[k]);
  return copy;
};

const SalesDataLinkage: React.FC<Props> = ({ isEditable = true, namespace, projectId, onReflect, onDirtyChange }) => {
  const { t } = useTranslation();
  const tr = (key: string) => t(`features.InvestmentScreening.${namespace}.salesDataLinkage.${key}`);

  const [state, setState] = useState<SalesDataLinkageInputState>(INITIAL_STATE);
  // サーバーから復元した直後の状態（未保存判定の基準値。ロード自体はユーザー編集ではないため）
  const baselineRef = useRef<SalesDataLinkageInputState>(INITIAL_STATE);

  // 初期状態（またはサーバー復元直後の状態）からの変更有無を判定し、親へ通知する
  const isDirty = useMemo(
    () => JSON.stringify(stripForDirtyCheck(state)) !== JSON.stringify(stripForDirtyCheck(baselineRef.current)),
    [state]
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
    // アンマウント時は破棄済みとして親のdirty状態をクリアする
    return () => onDirtyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  // 保存確定時（markAsSaved=true）は、更新後のstateをそのまま未保存判定の新しい基準値とする
  const handleChange = (patch: Partial<SalesDataLinkageInputState>, markAsSaved = false) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      if (markAsSaved) baselineRef.current = next;
      return next;
    });
  };

  const WebAPI = useContext(WebAPIContext);
  const projectContext = useContext(ProjectContext);
  const reviewDate = projectContext?.reviewDate ?? null;
  const toast = useToast();

  // ---- サーバーに保存済みの入力内容/AI分析結果を復元（営業データ連携タブ表示時に1回のみ） ----
  useEffect(() => {
    if (!projectId || state.hasLoadedFromServer) return;
    let cancelled = false;
    (async () => {
      try {
        const [infoRes, chartRes] = await Promise.all([
          WebAPI.request({
            query: `
              query getProjectAdditionalInfoById($projectId: String, $reviewDate: String) {
                getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
                  updateDate
                }
              }
            `,
            variables: { projectId, ...(reviewDate && { reviewDate }) },
          }),
          WebAPI.request({
            query: `
              query getLargeChartDataById($projectId: String!, $targets: [String!]!, $reviewDate: String) {
                getLargeChartDataById(projectId: $projectId, targets: $targets, reviewDate: $reviewDate) {
                  salesDataLinkageInputData
                  salesDataLinkageAnalysisData
                }
              }
            `,
            variables: {
              projectId,
              targets: ["salesDataLinkageInputData", "salesDataLinkageAnalysisData"],
              ...(reviewDate && { reviewDate }),
            },
          }),
        ]);
        if (cancelled) return;

        const updateDate: string | null = infoRes?.data?.getProjectAdditionalInfoById?.updateDate ?? null;
        const chartData = chartRes?.data?.getLargeChartDataById;
        const patch: Partial<SalesDataLinkageInputState> = { updateDate, hasLoadedFromServer: true };

        if (chartData?.salesDataLinkageInputData) {
          try {
            const parsed = JSON.parse(chartData.salesDataLinkageInputData) as {
              axisInclude?: Partial<Record<string, boolean>>;
              searched?: Partial<Record<string, boolean>>;
              filterValues?: Record<string, string>;
              resultMetric?: string;
              axes?: { key: string; rating?: number | null; note?: string }[];
              advantage?: { key: string; rating?: number | null; note?: string; radios?: Record<string, string> }[];
              overall?: { rating?: number | null; note?: string; confidence?: number };
            };
            const axisEvals: SalesDataLinkageInputState["axisEvals"] = { ...state.axisEvals };
            const axisNotes: SalesDataLinkageInputState["axisNotes"] = { ...state.axisNotes };
            (parsed.axes ?? []).forEach((a) => {
              if (a.rating != null) axisEvals[a.key as keyof typeof axisEvals] = a.rating as never;
              if (a.note) axisNotes[a.key as keyof typeof axisNotes] = a.note;
            });
            const evals: SalesDataLinkageInputState["evals"] = { ...state.evals };
            const advNotes: SalesDataLinkageInputState["advNotes"] = { ...state.advNotes };
            const advRadios: SalesDataLinkageInputState["advRadios"] = { ...state.advRadios };
            (parsed.advantage ?? []).forEach((item) => {
              if (item.rating != null) evals[item.key as keyof typeof evals] = item.rating as never;
              if (item.note) advNotes[item.key as keyof typeof advNotes] = item.note;
              Object.entries(item.radios ?? {}).forEach(([rgKey, v]) => {
                advRadios[`${item.key}.${rgKey}`] = v;
              });
            });
            patch.axisEvals = axisEvals;
            patch.axisNotes = axisNotes;
            patch.evals = evals;
            patch.advNotes = advNotes;
            patch.advRadios = advRadios;
            if (parsed.axisInclude) {
              patch.axisInclude = { ...state.axisInclude, ...parsed.axisInclude } as SalesDataLinkageInputState["axisInclude"];
            }
            if (parsed.searched) {
              patch.searched = { ...state.searched, ...parsed.searched } as SalesDataLinkageInputState["searched"];
            }
            if (parsed.filterValues) {
              const { STARTYM, ENDYM, ...restFilterValues } = parsed.filterValues;
              patch.filterValues = { ...state.filterValues, ...restFilterValues };
              if (STARTYM) patch.startYm = STARTYM;
              if (ENDYM) patch.endYm = ENDYM;
            }
            if (parsed.resultMetric) {
              patch.resultMetric = parsed.resultMetric as SalesDataLinkageInputState["resultMetric"];
            }
            if (parsed.overall) {
              if (parsed.overall.rating != null) patch.overallRating = parsed.overall.rating as SalesDataLinkageInputState["overallRating"];
              if (parsed.overall.note) patch.overallNote = parsed.overall.note;
              if (parsed.overall.confidence != null) patch.confidence = parsed.overall.confidence;
            }
          } catch (e) {
            console.error("Failed to parse salesDataLinkageInputData", e);
          }
        }

        if (chartData?.salesDataLinkageAnalysisData) {
          try {
            const parsedAnalysis = JSON.parse(chartData.salesDataLinkageAnalysisData) as {
              aiFactors?: FactorDef[];
              aiOverallNote?: string;
            };
            if (parsedAnalysis.aiFactors && parsedAnalysis.aiFactors.length > 0) {
              patch.aiFactors = parsedAnalysis.aiFactors;
              patch.aiOverallNote = parsedAnalysis.aiOverallNote ?? "";
              patch.analysisStatus = "complete";
              patch.aiRun = true;
            }
          } catch (e) {
            console.error("Failed to parse salesDataLinkageAnalysisData", e);
          }
        }

        baselineRef.current = { ...state, ...patch };
        handleChange(patch);
      } catch (e) {
        console.error("Failed to load saved salesDataLinkage analysis", e);
        if (!cancelled) {
          baselineRef.current = { ...state, hasLoadedFromServer: true };
          handleChange({ hasLoadedFromServer: true });
          toast({ description: tr("label.aiLoadError"), status: "warning", duration: 4000 });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // サーバーからの復元処理が完了するまでは入力内容の描画を保留する（復元前の初期表示がちらつく不具合の対策）
  const isRestoring = !!projectId && !state.hasLoadedFromServer;

  const currentIndex = SUB_TAB_DEFS.findIndex((s) => s.id === state.subView);

  const isStepComplete = (id: SalesDataLinkageSubView): boolean => {
    if ((AXIS_ORDER as string[]).includes(id)) return isAxisStepComplete(state, id as any);
    if (id === "advantage") return isAdvantageStepComplete(state);
    if (id === "confirm") return isConfirmStepComplete(state);
    return true;
  };

  const canGoNext = isStepComplete(state.subView) && currentIndex < SUB_TAB_DEFS.length - 1;
  const canGoPrev = currentIndex > 0;

  /** 「次へ」が押せない場合に、次に進むために必要な操作を案内するヒント文言 */
  const nextHint = (): string | null => {
    if (currentIndex >= SUB_TAB_DEFS.length - 1) return null;
    const id = state.subView;
    if ((AXIS_ORDER as string[]).includes(id)) {
      const axisKey = id as AxisKey;
      if (!state.axisInclude[axisKey]) return null;
      if (!state.searched[axisKey]) return tr("label.nextHintSearch");
      if (!state.axisEvals[axisKey]) return tr("label.nextHintEval");
      return null;
    }
    if (id === "advantage") return isAdvantageStepComplete(state) ? null : tr("label.nextHintEval");
    if (id === "confirm") return state.overallRating === null ? tr("label.nextHintOverall") : null;
    return null;
  };

  const rootRef = useRef<HTMLDivElement>(null);
  // 実際のスクロールは親のoverflowY:auto指定コンテナが担っているため、windowではなくその祖先要素を先頭に戻す
  const scrollToTop = () => {
    let el: HTMLElement | null = rootRef.current;
    while (el) {
      const { overflowY } = window.getComputedStyle(el);
      if (overflowY === "auto" || overflowY === "scroll") {
        el.scrollTop = 0;
        return;
      }
      el = el.parentElement;
    }
    window.scrollTo({ top: 0 });
  };

  const goTo = (id: SalesDataLinkageSubView) => handleChange({ subView: id });
  const goNext = () => {
    if (currentIndex < SUB_TAB_DEFS.length - 1) goTo(SUB_TAB_DEFS[currentIndex + 1].id);
    scrollToTop();
  };
  const goPrev = () => {
    if (currentIndex > 0) goTo(SUB_TAB_DEFS[currentIndex - 1].id);
    scrollToTop();
  };

  return (
    <Box mt={4} ref={rootRef}>
      {/* 機能ヘッダー */}
      <Flex
        align="flex-start"
        gap="12px"
        mb="16px"
        p="20px 22px"
        borderRadius="12px"
        border="1px solid #e2e8f0"
        bgGradient="linear(180deg, #f7f9fc 0%, #ffffff 100%)"
      >
        <Box w="6px" h="22px" mt="2px" borderRadius="3px" bg="#7c5cff" flexShrink={0} />
        <Box>
          <Flex align="center" gap="8px" mb="6px">
            <Text fontSize="16px" fontWeight={700} color="#1e293b">
              {tr("header.title")}
            </Text>
            <Box
              as="span"
              display="inline-block"
              fontSize="11px"
              fontWeight={700}
              color="#7c5cff"
              bg="#efeaff"
              borderRadius="10px"
              px="8px"
              py="2px"
            >
              {tr("header.badge")}
            </Box>
          </Flex>
          <Text fontSize="13px" color="#475569" lineHeight="1.8" whiteSpace="pre-line">
            {tr("header.description")}
          </Text>
        </Box>
      </Flex>

      {/* シェブロン型ステッパー（現在位置の表示のみ。タブ操作は不可） */}
      <HStack spacing="0" mb="18px" flexWrap="wrap">
        {SUB_TAB_DEFS.map((s, idx) => {
          const active = s.id === state.subView;
          const done = idx < currentIndex;
          return (
            <Box
              key={s.id}
              h="38px"
              minW="112px"
              px="18px"
              ml={idx === 0 ? 0 : "-14px"}
              fontSize="13px"
              fontWeight={600}
              whiteSpace="nowrap"
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{ clipPath: chevronClip(idx === 0, idx === SUB_TAB_DEFS.length - 1) }}
              bg={active ? "#16233f" : done ? "#c3d5ef" : "#e9edf3"}
              color={active ? "#fff" : done ? "#1e3a63" : "#7d8aa0"}
              position="relative"
              zIndex={SUB_TAB_DEFS.length - idx}
            >
              {idx + 1}. {tr(`tab.${s.labelKey}`)}
            </Box>
          );
        })}
      </HStack>

      {isRestoring ? (
        <Flex align="center" justify="center" minH="200px" gap="10px" color="#64748b">
          <Spinner size="sm" />
          <Text fontSize="13px">{tr("label.restoring")}</Text>
        </Flex>
      ) : (
        <fieldset disabled={!isEditable}>
          {(AXIS_ORDER as SalesDataLinkageSubView[]).includes(state.subView) && (
            <AxisStep
              axisKey={state.subView as any}
              state={state}
              isEditable={isEditable}
              namespace={namespace}
              onChange={handleChange}
              projectId={projectId}
            />
          )}
          {state.subView === "advantage" && (
            <AdvantageStep state={state} isEditable={isEditable} namespace={namespace} onChange={handleChange} />
          )}
          {state.subView === "confirm" && (
            <ConfirmStep state={state} isEditable={isEditable} namespace={namespace} onChange={handleChange} />
          )}
          {state.subView === "ai" && (
            <AiStep state={state} isEditable={isEditable} namespace={namespace} projectId={projectId} onChange={handleChange} onReflect={onReflect} />
          )}
        </fieldset>
      )}

      <Flex direction="column" align="flex-end" mt="18px">
        <HStack spacing="10px">
          {currentIndex > 0 && (
            <Button variant="outline" leftIcon={<ChevronLeftIcon />} isDisabled={!canGoPrev} onClick={goPrev}>
              {tr("button.previous")}
            </Button>
          )}
          {currentIndex < SUB_TAB_DEFS.length - 1 && (
            <Button
              colorScheme="blue"
              rightIcon={<ChevronRightIcon />}
              isDisabled={!canGoNext}
              onClick={goNext}
            >
              {tr("button.next")}
            </Button>
          )}
        </HStack>
        {nextHint() && (
          <Text fontSize="12px" color="#3182ce" mt="6px">
            {nextHint()}
          </Text>
        )}
      </Flex>
    </Box>
  );
};

export default SalesDataLinkage;
